import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { search } from '../lib/api';
import { useShortcuts } from '../lib/hooks';
import type { Theme } from '../lib/shiki';
import type { SearchFilters, SearchResponse } from '../lib/types';
import { toast } from '../lib/toast';
import { FiltersSidebar } from '../components/FiltersSidebar';
import { ResultRow } from '../components/ResultRow';

interface Props {
  theme: Theme;
}

/** The main search page. State machine:
 *  - filters live here so the sidebar can write to them
 *  - the in-flight query is stored in `query` (string in the input box) plus `submitted`
 *    (the string that was last submitted — driving the actual fetch)
 *  - results come from useMutation rather than useQuery because we only want to fetch on
 *    explicit submit, not on every filter toggle. The useQuery cache still helps for the
 *    repo+branch lookups in the sidebar. */
export function SearchPage({ theme }: Props) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [focused, setFocused] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null); // result_id
  const [filters, setFilters] = useState<SearchFilters>({
    repos: [],
    repo_kinds: [],
    languages: [],
    min_score: 0,
    branches: {},
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // The search uses useQuery keyed by (submitted, filters) — that way refining a filter and
  // hitting Search again is a free cache hit. Pure useQuery on (query, filters) would refetch
  // every keystroke, which is too expensive.
  const queryKey = useMemo(() => ['search', submitted, filters] as const, [submitted, filters]);
  const searchQuery = useQuery<SearchResponse>({
    queryKey,
    queryFn: () => search(submitted, filters, 20),
    enabled: submitted.length > 0,
  });

  const results = searchQuery.data?.results ?? [];

  // Reverse-similar / explain: re-search with the focused chunk's code as the query. The MCP
  // server semantically maps "code -> similar code" the same way it does for natural-language
  // queries (it's the same model + the same query prefix), so this gives the user a one-key
  // way to ask "show me other things that look like this".
  const explainMutation = useMutation({
    mutationFn: (code: string) => search(code, filters, 20),
    onSuccess: (data, code) => {
      const newSubmitted = code.slice(0, 200);
      // Pre-seed the cache for the key the next render will use, so the result is already
      // there when `submitted` updates — no second fetch. Must match the `queryKey` shape
      // of the search useQuery: ['search', submitted, filters].
      queryClient.setQueryData(['search', newSubmitted, filters], data);
      setSubmitted(newSubmitted);
      toast(`reverse-similar: ${data.results.length} hits`, 'info');
    },
  });

  useShortcuts({
    '/': {
      handler: (e) => {
        e.preventDefault();
        inputRef.current?.focus();
      },
    },
    j: () => {
      if (results.length === 0) return;
      setFocused((f) => Math.min(f + 1, results.length - 1));
    },
    k: () => {
      if (results.length === 0) return;
      setFocused((f) => Math.max(f - 1, 0));
    },
    Enter: () => {
      const r = results[focused];
      if (!r) return;
      setExpanded(expanded === r.result_id ? null : r.result_id);
    },
    e: () => {
      const r = results[focused];
      if (!r) return;
      // Cap the snippet sent through — embedding very long inputs adds latency for no quality win.
      explainMutation.mutate(r.code.slice(0, 1500));
    },
  });

  // Clamp the focused index whenever the result set shrinks.
  useEffect(() => {
    if (focused >= results.length) setFocused(Math.max(0, results.length - 1));
  }, [results.length, focused]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(query.trim());
    setExpanded(null);
    setFocused(0);
  };

  // Cross-stack signal: badge the result list when hits span both backend and frontend.
  const kinds = new Set(results.map((r) => r.repo_kind));
  const crossStack = kinds.has('backend') && kinds.has('frontend');

  return (
    <div className="flex flex-1 min-h-0">
      <FiltersSidebar filters={filters} setFilters={setFilters} />
      <main className="flex-1 min-w-0 overflow-y-auto px-4 py-4">
        <form onSubmit={onSubmit} className="flex gap-2 mb-4">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the codebase…   (press / to focus)"
            className="flex-1 rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 py-2 text-sm font-mono"
          />
          <button
            type="submit"
            disabled={!query.trim() || searchQuery.isFetching}
            className="rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2"
          >
            {searchQuery.isFetching ? 'Searching…' : 'Search'}
          </button>
        </form>

        {searchQuery.data?.advisory && (
          <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-100 px-3 py-2 text-sm mb-3">
            {searchQuery.data.advisory}
          </div>
        )}
        {searchQuery.data?.branches_used?.some((b) => b.served !== b.requested) && (
          <div className="rounded border border-fuchsia-300 dark:border-fuchsia-700 bg-fuchsia-50 dark:bg-fuchsia-950 text-fuchsia-900 dark:text-fuchsia-100 px-3 py-2 text-xs font-mono mb-3">
            Branch fallback in use — see badges below.
          </div>
        )}
        {crossStack && (
          <div className="rounded border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-100 px-3 py-2 text-xs mb-3">
            cross-stack: results span both backend and frontend repos.
          </div>
        )}

        {searchQuery.isError && (
          <div className="text-sm text-rose-600">
            Search failed: {(searchQuery.error as Error).message}
          </div>
        )}

        {submitted && !searchQuery.isFetching && results.length === 0 && (
          <div className="text-sm text-zinc-500">No results.</div>
        )}

        <div className="space-y-3">
          {results.map((hit, i) => (
            <div
              key={hit.result_id}
              onClick={() => setFocused(i)}
              className={clsx('cursor-default')}
            >
              <ResultRow
                hit={hit}
                query={submitted}
                focused={i === focused}
                expanded={expanded === hit.result_id}
                theme={theme}
                onFocus={() => setFocused(i)}
                onToggleExpand={() =>
                  setExpanded(expanded === hit.result_id ? null : hit.result_id)
                }
              />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
